import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, MapPin, Star, Building, BarChart3, Globe } from "lucide-react";
import { HospitalEditModal } from "./HospitalEditModal";

interface Hospital {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  shame_score: number;
  total_procedures: number;
  avg_markup_percentage: number;
  followed: boolean;
  data_obfuscation_score?: number;
  avg_file_size_mb?: number;
  file_complexity_score?: number;
  total_chargemaster_files?: number;
  website_url?: string;
  transparency_portal_url?: string;
  shoppable_services_url?: string;
  chargemaster_url?: string;
  npi_number?: string;
  procedure_count?: number;
  transparency_grade?: string;
  [key: string]: any;
}

interface MapAdminPanelProps {
  selectedHospital: Hospital | null;
  isAdmin: boolean;
  onHospitalUpdate?: (hospital: Hospital) => void;
}

export const MapAdminPanel = ({ selectedHospital, isAdmin, onHospitalUpdate }: MapAdminPanelProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  if (!isAdmin || !selectedHospital) {
    return null;
  }

  const getShameScoreColor = (score: number) => {
    if (score >= 90) return "destructive";
    if (score >= 75) return "secondary";
    if (score >= 60) return "outline";
    return "default";
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleSaveHospital = (updatedHospital: Hospital) => {
    onHospitalUpdate?.(updatedHospital);
  };

  return (
    <>
      <Card className="w-80 bg-background/95 backdrop-blur-sm border border-border shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold text-primary flex items-center gap-2">
                <Building className="w-5 h-5" />
                Admin Panel
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedHospital.name}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleEditClick}
              className="ml-2 shrink-0"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Hospital Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Follow Status</span>
              <Badge variant={selectedHospital.followed ? "default" : "secondary"}>
                {selectedHospital.followed ? "Following" : "Not Following"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Shame Score</span>
              <Badge variant={getShameScoreColor(selectedHospital.shame_score)}>
                {selectedHospital.shame_score}/100
              </Badge>
            </div>
            
            {selectedHospital.data_obfuscation_score !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Obfuscation Score</span>
                <Badge variant="outline">
                  {selectedHospital.data_obfuscation_score}/10
                </Badge>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-muted/50 rounded-lg p-2">
              <div className="text-xs text-muted-foreground">Procedures</div>
              <div className="text-sm font-semibold">{selectedHospital.total_procedures || 0}</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-2">
              <div className="text-xs text-muted-foreground">Files</div>
              <div className="text-sm font-semibold">{selectedHospital.total_chargemaster_files || 0}</div>
            </div>
          </div>

          {/* Location Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {selectedHospital.city}, {selectedHospital.state}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Coordinates: {selectedHospital.latitude.toFixed(6)}, {selectedHospital.longitude.toFixed(6)}
            </div>
          </div>

          {/* External Links */}
          <div className="space-y-2">
            {selectedHospital.website_url && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => window.open(selectedHospital.website_url, '_blank')}
              >
                <Globe className="w-4 h-4 mr-2" />
                Website
              </Button>
            )}
            
            {selectedHospital.transparency_portal_url && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => window.open(selectedHospital.transparency_portal_url, '_blank')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Transparency Portal
              </Button>
            )}
            
            {selectedHospital.chargemaster_url && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => window.open(selectedHospital.chargemaster_url, '_blank')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Chargemaster
              </Button>
            )}
          </div>

          {/* NPI Information */}
          {selectedHospital.npi_number && (
            <div className="text-xs text-muted-foreground">
              NPI: {selectedHospital.npi_number}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <HospitalEditModal
        hospital={selectedHospital}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveHospital}
      />
    </>
  );
};